import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { api } from "../lib/api";
import { useContent } from "../lib/content";

const HERO_IMG = "/buyerhub/hero.jpg";

const SELLER_FEATURES = [
  { num: "01", title: "Prep with confidence", blurb: "A clear, room-by-room guide to get your home show-ready." },
  { num: "02", title: "Get expert advice", blurb: "Ask your agent anything — replies land in your inbox." },
  { num: "03", title: "Handle the paperwork", blurb: "Complete your listing forms and disclosures in one place." },
  { num: "04", title: "Track every date", blurb: "From listing day to closing, see every key date in order." },
];

export default function Landing() {
  const nav = useNavigate();
  const { content } = useContent();
  const [inviteOpen, setInviteOpen] = useState(false);

  // Front door is always seller-themed. Buyer experience is post-login only.
  const brand = content?.branding?.product_name || "PorchLight";
  const agentName = content?.branding?.agent_name || "your agent";
  const agentEmail = content?.branding?.agent_email || "";
  const FEATURES = SELLER_FEATURES;
  const heroOverline = "Your home selling companion";
  const heroVerb = "Sell";
  const heroBlurb = "A simple place to prep your home, complete your listing paperwork, and track every date from listing day to closing.";
  const referralOverline = "Know a seller?";
  const referralBlurb = `Know someone thinking about selling? Send a friend an invitation and ${agentName} will reach out personally \u2014 no sign-up required from you.`;
  const footerTagline = `Your home selling companion with ${agentName}.`;

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      {/* Top nav */}
      <header style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(248,246,243,0.85)", backdropFilter: "blur(10px)" }}>
        <div className="container-x" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 72 }}>
          <span className="font-serif" data-testid="brand-link" style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em" }}>{brand}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button data-testid="login-btn-nav" className="btn-ghost" onClick={() => nav("/login")}>Sign in</button>
            <button data-testid="get-started-btn-nav" className="btn-primary" onClick={() => nav("/register")}>
              Get Started <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section style={{ padding: "72px 0 96px" }}>
        <div className="container-x" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)", gap: 80, alignItems: "center" }}>
          <div>
            <span className="overline" data-testid="hero-overline" style={{ borderTop: "1px solid var(--text)", paddingTop: 8, display: "inline-block" }}>
              {heroOverline}
            </span>
            <h1 className="font-serif" style={{ fontSize: "clamp(3rem, 6.4vw, 5rem)", lineHeight: 1.02, letterSpacing: "-0.02em", margin: "28px 0 28px", fontWeight: 500 }}>
              {heroVerb} with <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>Clarity</em><br />and Confidence.
            </h1>
            <p style={{ fontSize: "1.05rem", color: "var(--text-muted)", maxWidth: 480, lineHeight: 1.6, margin: 0 }}>
              {heroBlurb}
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 40, flexWrap: "wrap" }}>
              <button data-testid="hero-get-started-btn" className="btn-primary" onClick={() => nav("/register")}>
                Get Started <ArrowRight size={16} />
              </button>
              <button data-testid="hero-login-btn" className="btn-secondary" onClick={() => nav("/login")}>
                I already have an account
              </button>
            </div>
          </div>
          <div>
            <div style={{ borderRadius: 4, overflow: "hidden", aspectRatio: "4/5", background: "var(--surface-alt)" }}>
              <img src={HERO_IMG} alt="Warm home interior" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section style={{ padding: "64px 0 120px" }}>
        <div className="container-x">
          <div style={{ maxWidth: 760 }}>
            <span className="overline" style={{ borderTop: "1px solid var(--text)", paddingTop: 8, display: "inline-block" }}>What&rsquo;s inside</span>
            <h2 className="font-serif" style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)", marginTop: 24, letterSpacing: "-0.02em", fontWeight: 500, lineHeight: 1.05 }}>
              Everything you need,{" "}
              <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>nothing you don&rsquo;t.</em>
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 56, marginTop: 88 }}>
            {FEATURES.map((f) => (
              <div key={f.num} data-testid={`feature-${f.num}`}>
                <div style={{ borderTop: "1px solid var(--text)", paddingTop: 8, display: "inline-block" }}>
                  <span className="overline" style={{ fontSize: 11 }}>{f.num}</span>
                </div>
                <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em", margin: "12px 0 8px", lineHeight: 1.2 }}>{f.title}</h3>
                <p style={{ color: "var(--text-muted)", lineHeight: 1.55, margin: 0, fontSize: 14 }}>{f.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pass to a friend */}
      <section style={{ padding: "0 0 120px" }}>
        <div className="container-x">
          <div style={{ background: "var(--primary)", color: "#fff", borderRadius: 8, padding: "clamp(40px, 7vw, 80px)", display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 0.8fr)", gap: 48, alignItems: "center" }} className="referral-band">
            <div>
              <span className="overline" style={{ color: "rgba(255,255,255,0.7)", borderTop: "1px solid rgba(255,255,255,0.4)", paddingTop: 8, display: "inline-block" }}>{referralOverline}</span>
              <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)", marginTop: 20, letterSpacing: "-0.02em", fontWeight: 500, lineHeight: 1.08 }}>
                Pass <em style={{ fontStyle: "italic", color: "#E8B4A0" }}>{brand}</em> to a friend.
              </h2>
              <p style={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.6, margin: "16px 0 0", maxWidth: 460 }}>
                {referralBlurb}
              </p>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button data-testid="landing-send-invite-btn" onClick={() => setInviteOpen(true)}
                style={{ background: "#fff", color: "var(--primary)", border: "none", borderRadius: 9999, padding: "14px 28px", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                Send invite <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid var(--border)", padding: "40px 0 48px", color: "var(--text-muted)", fontSize: 14 }}>
        <div className="container-x" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div className="font-serif" style={{ fontSize: 22, color: "var(--text)", fontWeight: 500 }}>{brand}</div>
            <div style={{ marginTop: 4 }}>{footerTagline}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div>© {new Date().getFullYear()} {brand}</div>
            {agentEmail && <a href={`mailto:${agentEmail}`} style={{ color: "var(--text)" }}>{agentEmail}</a>}
          </div>
        </div>
      </footer>

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} />}
    </div>
  );
}

function InviteModal({ onClose }) {
  const [form, setForm] = useState({ referrer_name: "", referrer_email: "", friend_name: "", friend_email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/referrals", form);
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,25,23,0.5)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" data-testid="public-referral-form" style={{ background: "var(--surface)", width: "100%", maxWidth: 480, padding: 32, position: "relative" }}>
        <button onClick={onClose} className="btn-ghost" data-testid="referral-close-btn" style={{ position: "absolute", top: 16, right: 16, padding: 6 }}><X size={18} /></button>
        {done ? (
          <div data-testid="referral-success" style={{ textAlign: "center", padding: "20px 0" }}>
            <h3 className="font-serif" style={{ fontSize: 26, fontWeight: 500, marginBottom: 8 }}>Invitation on its way.</h3>
            <p style={{ color: "var(--text-muted)", lineHeight: 1.6 }}>Thanks — we&rsquo;ll reach out to your friend personally.</p>
            <button className="btn-primary" onClick={onClose} style={{ marginTop: 20, justifyContent: "center" }}>Done</button>
          </div>
        ) : (
          <>
            <span className="overline">Refer a friend</span>
            <h3 className="font-serif" style={{ fontSize: 26, fontWeight: 500, margin: "8px 0 20px" }}>Send an invitation.</h3>
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <FieldMini label="Your name" testid="ref-referrer-name" value={form.referrer_name} onChange={u("referrer_name")} required />
                <FieldMini label="Your email" testid="ref-referrer-email" type="email" value={form.referrer_email} onChange={u("referrer_email")} required />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <FieldMini label="Friend's name" testid="ref-friend-name" value={form.friend_name} onChange={u("friend_name")} required />
                <FieldMini label="Friend's email" testid="ref-friend-email" type="email" value={form.friend_email} onChange={u("friend_email")} required />
              </div>
              <label style={{ fontSize: 13, fontWeight: 500 }}>
                A note (optional)
                <textarea data-testid="ref-note" className="input-field" style={{ marginTop: 6, minHeight: 72 }} value={form.note} onChange={u("note")} placeholder="A quick hello for your friend." />
              </label>
              {err && <div style={{ color: "var(--error)", fontSize: 13 }}>{err}</div>}
              <button type="submit" className="btn-primary" data-testid="ref-submit-btn" disabled={busy} style={{ justifyContent: "center", marginTop: 4 }}>
                {busy ? "Sending…" : "Send invite"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function FieldMini({ label, testid, type = "text", value, onChange, required }) {
  return (
    <label style={{ fontSize: 13, fontWeight: 500 }}>
      {label}
      <input data-testid={testid} className="input-field" style={{ marginTop: 6 }} type={type} value={value} onChange={onChange} required={required} />
    </label>
  );
}
